import { DocumentProviderType, sharePointConnectionId } from 'models/integrations/documentProvider';

export enum DocumentConnectionStatus {
  Disabled = 0,
  Enabled = 1,
}

// Mirrored by numeric value from the API (DocumentConnection.DocumentConnectionAuthModes)
export enum DocumentConnectionAuthMode {
  // the organization's credential on the connection: a service account key, an app password
  TenantCredential = 0,
  // Google Drive: REDLAB's service account acts as the signed-in Workspace user; no secret on the row
  DomainDelegated = 1,
  // Nextcloud: the row holds the OAuth2 client and each user grants their own account once
  UserDelegated = 2,
}

// Whether the signed-in user can use a connection now (the API's DocumentConnectionAccessStatus)
export enum DocumentConnectionUserStatus {
  Connected = 0,
  AuthorizationRequired = 1,
  NotAvailable = 2,
}

// Whether the signed-in user's library reads can run on a connection: only with their own stored grant, because a run
// has no sign-in (the API's DocumentReadAccessStatus, phase 28)
export enum DocumentReadAccessStatus {
  Granted = 0,
  // not connected yet: Connect
  GrantRequired = 1,
  // the provider refused the stored grant: Reconnect
  NeedsReauthorization = 2,
  // the connection reads with the organization's credential, never used for library reads (DOCPROV0016)
  NotSupported = 3,
  NotAvailable = 4,
}

// Whose identity a provider call runs with (the API's DocumentActingIdentity)
export enum DocumentActingIdentity {
  User = 0,
  Organization = 1,
}

// The non-secret configuration of a Nextcloud connection
export interface INextcloudConnectionConfig {
  baseUrl?: string;
  username?: string;
  clientId?: string;
}

// The non-secret configuration of a Google Drive connection
export interface IGoogleDriveConnectionConfig {
  clientEmail?: string;
  projectId?: string;
  domain?: string;
}

const parseConfig = <T extends object>(config: string | undefined): T => {
  if (!config) return {} as T;

  try {
    return (JSON.parse(config) ?? {}) as T;
  } catch {
    return {} as T;
  }
};

//
// An organization's connection to a document provider. The credentials never leave the server: authData is only
// sent to it, and hasAuthData says a secret is stored. userStatus, userStatusCode, actingIdentity, readStatus and
// readStatusCode describe the signed-in user's access and are set on the list only.
//
export default class DocumentConnection {
  documentConnectionId: number;

  providerType: DocumentProviderType;

  name: string;

  status: DocumentConnectionStatus;

  authMode: DocumentConnectionAuthMode;

  isAuthorized?: boolean;

  // non-secret provider configuration as JSON; set for an Admin only
  config?: string;

  // write-only: new credentials as JSON, undefined keeps the stored ones
  authData?: string;

  hasAuthData: boolean;

  lastTestedDate?: Date;

  created?: Date;

  createdById?: string;

  userStatus?: DocumentConnectionUserStatus;

  // the DOCPROV, IDP or AUTH code that explains AuthorizationRequired or NotAvailable
  userStatusCode?: string;

  actingIdentity?: DocumentActingIdentity;

  readStatus?: DocumentReadAccessStatus;

  // the DOCPROV code that explains a readStatus other than Granted
  readStatusCode?: string;

  constructor() {
    this.documentConnectionId = -1;
    this.providerType = DocumentProviderType.GoogleDrive;
    this.name = '';
    this.status = DocumentConnectionStatus.Enabled;
    this.authMode = DocumentConnectionAuthMode.TenantCredential;
    this.hasAuthData = false;
  }

  isNew(): boolean {
    return this.documentConnectionId < 0;
  }

  // the synthetic SharePoint and OneDrive entry: no edit; the user's read grant is stored under it
  isSharePoint(): boolean {
    return this.documentConnectionId === sharePointConnectionId;
  }

  // a connection each user connects their own account to
  isUserDelegated(): boolean {
    return !this.isSharePoint() && this.authMode === DocumentConnectionAuthMode.UserDelegated;
  }

  getNextcloudConfig(): INextcloudConnectionConfig {
    return parseConfig<INextcloudConnectionConfig>(this.config);
  }

  getGoogleDriveConfig(): IGoogleDriveConnectionConfig {
    return parseConfig<IGoogleDriveConnectionConfig>(this.config);
  }

  isEqual(item: DocumentConnection): boolean {
    if (item.documentConnectionId !== this.documentConnectionId) return false;
    if (item.providerType !== this.providerType) return false;
    if (item.name !== this.name) return false;
    if (item.status !== this.status) return false;
    if (item.authMode !== this.authMode) return false;
    if (item.config !== this.config) return false;
    // a newly typed secret is a change although the stored one is never returned
    if (item.authData !== this.authData) return false;

    return true;
  }

  clone(): DocumentConnection {
    const output = new DocumentConnection();
    output.documentConnectionId = this.documentConnectionId;
    output.providerType = this.providerType;
    output.name = this.name;
    output.status = this.status;
    output.authMode = this.authMode;
    output.isAuthorized = this.isAuthorized;
    output.config = this.config;
    output.authData = this.authData;
    output.hasAuthData = this.hasAuthData;
    output.lastTestedDate = this.lastTestedDate;
    output.created = this.created;
    output.createdById = this.createdById;
    output.userStatus = this.userStatus;
    output.userStatusCode = this.userStatusCode;
    output.actingIdentity = this.actingIdentity;
    output.readStatus = this.readStatus;
    output.readStatusCode = this.readStatusCode;

    return output;
  }
}

// The signed-in user's own grant on a user-delegated connection; the tokens stay on the server
export class DocumentConnectionGrant {
  documentConnectionId: number;

  connectionName: string;

  providerType: DocumentProviderType;

  // false once the provider refused the stored tokens
  isAuthorized?: boolean;

  lastTestedDate?: Date;

  created?: Date;

  constructor() {
    this.documentConnectionId = -1;
    this.connectionName = '';
    this.providerType = DocumentProviderType.Nextcloud;
  }
}

export class DocumentConnectionTestResult {
  success: boolean;

  message?: string;

  // the DOCPROV, IDP or AUTH code of a failed test
  code?: string;

  accountDisplay?: string;

  constructor() {
    this.success = false;
  }
}

// One place to browse into at a provider: a OneDrive, a site library, a shared drive, a files root
export class ProviderContainer {
  id: string;

  name: string;

  kind?: string;

  hasChildren: boolean;

  // false when nothing can be saved in the container itself; a folder inside it must be chosen
  canBeRoot: boolean;

  constructor() {
    this.id = '';
    this.name = '';
    this.hasChildren = false;
    this.canBeRoot = true;
  }
}

export class ProviderItem {
  id: string;

  name: string;

  // passed back as folderId to descend; undefined for providers addressed by id
  browsePath?: string;

  isFolder: boolean;

  mimeType?: string;

  size?: number;

  modified?: Date;

  webUrl?: string;

  constructor() {
    this.id = '';
    this.name = '';
    this.isFolder = false;
  }
}

export class ProviderItemPage {
  items: ProviderItem[];

  // passed back unchanged for the next page; undefined on the last page
  nextPageToken?: string;

  constructor() {
    this.items = [];
  }
}
